import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Artwork from '../components/Artwork.jsx'
import ErrorState, { LoadingState } from '../components/ErrorState.jsx'
import { ApiError, api } from '../lib/api.js'
import { display } from '../lib/score.js'
import { plural } from '../lib/format.js'

/**
 * `/compare/:handle` — the Taste Graph's page (1B-3, PLAN §15).
 *
 * Numbers stay primary. The similarity is printed as itself, with `n`, the
 * confidence line, `algo_version` and when it was computed; the bucket label
 * is a caption *over* the number, never a replacement for it. All user-facing
 * strings below the constants are the owner-approved copy from the 1B-3 plan.
 *
 * An account the viewer may not see gets the same treatment the profile page
 * gives it: a quiet card and nothing else — no counts, because "how much you
 * two overlap" is a fact about a hidden account's rating activity.
 */

// §Approved copy: display buckets over sim_adj (per-mille). Order matters —
// first floor that holds wins.
const BUCKETS = [
  [850, 'Very close taste'],
  [650, 'Overlapping taste'],
  [350, 'Different centers'],
]

function bucketLabel(simAdj) {
  for (const [floor, label] of BUCKETS) {
    if (simAdj >= floor) return label
  }
  return 'Opposite poles'
}

/** 375 per-mille → "0.38". The one place the stored integer becomes prose. */
export function displaySim(perMille) {
  return (perMille / 1000).toFixed(2)
}

function SharedRow({ entry, otherName }) {
  const media = entry.media
  return (
    <li className="chart-row">
      {media ? (
        <Link
          to={`/${media.kind === 'song' ? 'song' : 'album'}/${media.public_id}`}
          className="chart-media"
        >
          <Artwork
            art={media.artwork_url ? { url: media.artwork_url } : null}
            title={media.title}
            artist={media.credit_name}
            size={48}
          />
          <span className="row-main">
            <span className="chart-title">{media.title}</span>
            <span className="footnote">
              {media.credit_name}
              {media.year ? ` · ${media.year}` : ''}
            </span>
          </span>
        </Link>
      ) : (
        <span className="row-main footnote">No longer in the catalog</span>
      )}
      <span className="chart-scores">
        <span className="compare-scores">
          You {display(entry.mine)} · {otherName} {display(entry.theirs)}
        </span>
      </span>
    </li>
  )
}

function SharedList({ title, items, otherName }) {
  if (!items.length) return null
  return (
    <section className="card chart fade-up">
      <header className="card-pad chart-head">
        <h2 className="title-3 mb-0">{title}</h2>
      </header>
      <ol className="chart-list">
        {items.map((entry, index) => (
          <SharedRow key={entry.media?.public_id || index} entry={entry} otherName={otherName} />
        ))}
      </ol>
    </section>
  )
}

export default function Compare() {
  const { handle } = useParams()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(
    (signal) => {
      setError('')
      return api
        .compare(handle, { signal })
        .then(setData)
        .catch((err) => {
          if (err.name === 'AbortError') return
          setError(err instanceof ApiError ? err.message : 'The comparison did not load.')
        })
    },
    [handle],
  )

  useEffect(() => {
    document.title = `Compare with @${handle} — Sonologue`
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [handle, load])

  if (error) {
    return (
      <ErrorState
        title="The comparison did not load"
        message={error}
        onRetry={() => load()}
        level={1}
      />
    )
  }
  if (!data) return <LoadingState label="Loading comparison" />

  const name = data.display_name

  if (data.is_me) {
    return (
      <div className="card card-pad fade-up">
        <div className="empty-state">
          <h2>That is you</h2>
          <p>A comparison takes two people — open somebody else&apos;s profile to start one.</p>
        </div>
      </div>
    )
  }

  if (!data.visible) {
    return (
      <div className="card card-pad fade-up">
        <div className="empty-state">
          <h2>@{data.username}</h2>
          <p>You cannot compare with this account.</p>
          <Link className="btn btn-quiet btn-sm" to={`/profile/${data.username}`}>
            View profile
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="section-header">
        <h1 className="title-2">
          You and{' '}
          <Link to={`/profile/${data.username}`} className="quiet-link">
            {name}
          </Link>
        </h1>
      </div>

      {data.published ? (
        <section className="card card-pad fade-up compare-headline">
          <p className="footnote mb-0">{bucketLabel(data.sim_adj)}</p>
          <p className="compare-number mb-0" aria-label={`Similarity ${displaySim(data.sim_adj)}`}>
            {displaySim(data.sim_adj)}
          </p>
          <p className="footnote mb-0">
            {data.confidence === 'solid'
              ? `Based on ${data.n} shared ratings`
              : `Based on ${data.n} shared ratings — early read`}
          </p>
        </section>
      ) : (
        <div className="card card-pad fade-up">
          <div className="empty-state">
            <h2>Fewer than five albums in common — Sonologue won&apos;t guess.</h2>
            <p>
              Similarity is published from five shared ratings on. Rate more of the same records
              and this page fills in.
            </p>
          </div>
        </div>
      )}

      {data.published ? (
        <div className="chart-stack">
          <SharedList title="Where you agree" items={data.agreements} otherName={name} />
          <SharedList title="Where you differ" items={data.disagreements} otherName={name} />
          {data.genre_slices.length ? (
            <section className="card chart fade-up">
              <header className="card-pad chart-head">
                <h2 className="title-3 mb-0">By genre</h2>
              </header>
              <ol className="chart-list">
                {data.genre_slices.map((slice) => (
                  <li className="chart-row" key={slice.genre}>
                    <span className="row-main chart-title">{slice.genre}</span>
                    <span className="chart-scores">
                      <span className="compare-scores">
                        {displaySim(slice.sim_adj)} · {slice.n} shared
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
      ) : null}

      <p className="footnote">
        Shared ratings only, both public: {data.n_albums} {plural(data.n_albums, 'album')} ·{' '}
        {data.n_songs} {plural(data.n_songs, 'song')}. Shrunk
        correlation over each person&apos;s own scale ·{' '}
        <span className="mono">{data.algo_version || '—'}</span>
        {data.computed_at ? ` · computed ${new Date(data.computed_at).toLocaleString()}` : ''}
      </p>
    </>
  )
}
